/**
 * BRXWrapperEntry
 *
 * Entry controller for the embeddables wrapper section.
 * Inspects BRXSession, decides which surface to render, and orchestrates
 * the fresh-intake boot sequence (overlay → flow detection → provider load).
 *
 * Entry modes:
 *   returning_checkout_ready  — valid checkout URL exists; show BRXCheckoutReturn
 *   returning_checkout_stale  — checkout complete but URL unresolvable; show BRXCheckoutReturn
 *   welcome_screen            — first visit (welcome not yet answered); show welcome → overlay → app
 *   fresh_intake              — welcome answered, no checkout; skip welcome + overlay, load app directly
 *
 * Deps are injected so each module can be tested in isolation.
 *
 * Usage (from section inline script):
 *   window.BRXWrapperEntry.start(sectionRoot, {
 *     overlay:            window.BRXWrapperOverlay,
 *     loader:             window.BRXEmbeddablesLoader,
 *     returnUi:           window.BRXCheckoutReturn,
 *     flow:               window.BRXFlowRuntime,
 *     welcome:            window.BRXWelcomeScreen,
 *     checkoutSubmitting: window.BRXCheckoutSubmitting
 *   });
 */
(function () {
    var SOFT_WAIT_MS    = 10000;
    var MAX_WAIT_MS     = 20000;
    var WARMUP_MS       = 5000;
    var SUBMIT_TIMEOUT_MS = 20000;

    // --------------------------------------------------
    // Editor detection (kept here — affects entry decision)
    // --------------------------------------------------

    function isEditorMode() {
        try { if (window.Shopify && window.Shopify.designMode) return true; } catch (e) {}
        try {
            var sp = new URLSearchParams(window.location.search);
            if (
                sp.has('preview_theme_id') ||
                sp.has('design_theme_id') ||
                sp.has('pb') ||
                sp.get('_fd') === '0'
            ) return true;
        } catch (e) {}
        try { if (/^admin\.shopify\.com$/i.test(window.location.hostname)) return true; } catch (e) {}
        return false;
    }

    // --------------------------------------------------
    // Entry-mode decision
    // --------------------------------------------------

    function getEntryMode() {
        var session = window.BRXSession;
        if (!session || !session.getCheckoutReturnState) return 'welcome_screen';
        var rs = session.getCheckoutReturnState();
        if (rs.mode === 'ready') return 'returning_checkout_ready';
        if (rs.mode === 'stale') return 'returning_checkout_stale';
        var state = session.getState ? session.getState() : {};
        if (state.brxFlowPage === 'savvy' || state.brxFlowPage === 'checkout_ready') return 'fresh_intake';
        return 'welcome_screen';
    }

    // --------------------------------------------------
    // Public: start
    // --------------------------------------------------

    function start(root, deps) {
        // --- Guard: Shopify editor (no session or deps needed) --------------
        if (isEditorMode()) {
            var welcomeEl = root.querySelector('[data-intake-welcome]');
            if (welcomeEl) welcomeEl.classList.remove('is-hidden');
            return;
        }

        if (!window.BRXSession) {
            window.addEventListener('brx:session-ready', function () {
                start(root, deps);
            }, { once: true });
            return;
        }

        deps = deps || {};
        var BRXWrapperOverlay     = deps.overlay            || window.BRXWrapperOverlay;
        var BRXEmbeddablesLoader  = deps.loader             || window.BRXEmbeddablesLoader;
        var BRXCheckoutReturn     = deps.returnUi           || window.BRXCheckoutReturn;
        var BRXFlowRuntime        = deps.flow               || window.BRXFlowRuntime;
        var BRXWelcomeScreen      = deps.welcome            || window.BRXWelcomeScreen;
        var BRXCheckoutSubmitting = deps.checkoutSubmitting || window.BRXCheckoutSubmitting;

        var savvyId     = root.getAttribute('data-savvy-id')    || '';
        var fallbackUrl = root.getAttribute('data-fallback-url') || '';

        // Create the overlay controller once; it may be reused after "Resume intake".
        var overlay = BRXWrapperOverlay ? BRXWrapperOverlay.create(root) : null;

        // Final-submit feedback (spinner overlay + error banner over the host).
        var submittingUI    = BRXCheckoutSubmitting ? BRXCheckoutSubmitting.mount(root) : null;
        var submitTimeoutId = null;

        function clearSubmitTimeout() {
            if (submitTimeoutId) { clearTimeout(submitTimeoutId); submitTimeoutId = null; }
        }

        // --- Guard: missing savvy ID ----------------------------------------
        if (!savvyId) {
            root.classList.add('is-warmup');
            if (overlay) {
                overlay.setHeading('Missing flow configuration');
                overlay.setMessage('Add a Savvy flow ID in the section settings.');
                overlay.setProgress(0);
            }
            return;
        }

        // --- Entry-mode branch ---------------------------------------------
        var entryMode = getEntryMode();

        if (entryMode === 'returning_checkout_ready' || entryMode === 'returning_checkout_stale') {
            if (BRXCheckoutReturn) {
                BRXCheckoutReturn.mount(root, {
                    mode: entryMode === 'returning_checkout_ready' ? 'ready' : 'stale',
                    onResume: function () {
                        startFreshIntake(root, {
                            overlay:     overlay,
                            loader:      BRXEmbeddablesLoader,
                            flow:        BRXFlowRuntime,
                            savvyId:     savvyId,
                            fallbackUrl: fallbackUrl,
                            skipOverlay: true
                        });
                    }
                });
            }
        } else if (entryMode === 'welcome_screen') {
            if (BRXWelcomeScreen) {
                BRXWelcomeScreen.mount(root, {
                    onAnswered: function () {
                        if (window.BRXSession && window.BRXSession.setBrxFlowPage) {
                            window.BRXSession.setBrxFlowPage('savvy');
                        }
                        window.scrollTo(0, 0);
                        startFreshIntake(root, {
                            overlay:     overlay,
                            loader:      BRXEmbeddablesLoader,
                            flow:        BRXFlowRuntime,
                            savvyId:     savvyId,
                            fallbackUrl: fallbackUrl,
                            skipOverlay: false
                        });
                    }
                });
            }
        } else {
            // fresh_intake — welcome already answered; show overlay while flow loads
            startFreshIntake(root, {
                overlay:     overlay,
                loader:      BRXEmbeddablesLoader,
                flow:        BRXFlowRuntime,
                savvyId:     savvyId,
                fallbackUrl: fallbackUrl,
                skipOverlay: false
            });
        }

        // --- Final-submit feedback -------------------------------------------
        // Checkout.js (running inside the Embeddables host) dispatches these
        // around its lead-update + session-complete calls. The spinner overlay
        // layers on top of the still-mounted host; the safety timer guards
        // against a spinner that never resolves if a request hangs silently.
        window.addEventListener('brx:checkout-submitting', function () {
            if (submittingUI) submittingUI.showSubmitting('Submitting prescription...');
            clearSubmitTimeout();
            submitTimeoutId = setTimeout(function () {
                if (submittingUI) submittingUI.showError('This is taking longer than expected. Please try again.');
            }, SUBMIT_TIMEOUT_MS);
        });

        window.addEventListener('brx:checkout-submitting-error', function (event) {
            clearSubmitTimeout();
            var message = event && event.detail && event.detail.message;
            if (submittingUI) {
                if (message) submittingUI.showError(message);
                else submittingUI.hideSubmitting();
            }
        });

        // --- In-session checkout completion ---------------------------------
        // brx-session.js dispatches this after POST /checkout/session resolves
        // (or immediately with the dosable URL as fallback). Mount the return
        // surface in place — no page navigation required.
        window.addEventListener('brx:checkout-ready', function () {
            clearSubmitTimeout();
            if (submittingUI) submittingUI.hideSubmitting();
            if (BRXCheckoutReturn) {
                BRXCheckoutReturn.mount(root, {
                    mode: 'ready',
                    onResume: function () {
                        startFreshIntake(root, {
                            overlay:     overlay,
                            loader:      BRXEmbeddablesLoader,
                            flow:        BRXFlowRuntime,
                            savvyId:     savvyId,
                            fallbackUrl: fallbackUrl,
                            skipOverlay: true
                        });
                    }
                });
            }
        }, { once: true });

        // --- bfcache restoration fix ----------------------------------------
        // When the user navigates to checkout and presses Back, the browser may
        // restore this page from bfcache in whatever visual state it was in at
        // departure. Re-evaluate session state on pageshow and snap the correct
        // surface into view.
        window.addEventListener('pageshow', function (event) {
            if (!event.persisted) return;
            var session = window.BRXSession;
            if (!session) return;

            var rs = session.getCheckoutReturnState();

            if (rs.mode === 'ready' || rs.mode === 'stale') {
                var overlayEl = root.querySelector('[data-intake-overlay]');
                var hostEl    = root.querySelector('[data-intake-host]');
                var welcomeEl = root.querySelector('[data-intake-welcome]');
                if (overlayEl) overlayEl.classList.add('is-hidden');
                if (hostEl)    hostEl.classList.add('is-hidden');
                if (welcomeEl) welcomeEl.classList.add('is-hidden');
                root.classList.remove('is-warmup');
                if (BRXCheckoutReturn) {
                    BRXCheckoutReturn.mount(root, {
                        mode: rs.mode === 'ready' ? 'ready' : 'stale',
                        onResume: function () {
                            startFreshIntake(root, {
                                overlay:     overlay,
                                loader:      BRXEmbeddablesLoader,
                                flow:        BRXFlowRuntime,
                                savvyId:     savvyId,
                                fallbackUrl: fallbackUrl,
                                skipOverlay: true
                            });
                        }
                    });
                }
            } else if (session.getState && session.getState().brxFlowPage !== 'welcome') {
                // Past welcome — ensure overlay and welcome are hidden.
                var overlayEl2 = root.querySelector('[data-intake-overlay]');
                var welcomeEl2 = root.querySelector('[data-intake-welcome]');
                if (overlayEl2) overlayEl2.classList.add('is-hidden');
                if (welcomeEl2) welcomeEl2.classList.add('is-hidden');
            }
        });
    }

    // --------------------------------------------------
    // Fresh-intake boot sequence
    // --------------------------------------------------

    function startFreshIntake(root, deps) {
        var overlay     = deps.overlay;
        var loader      = deps.loader;
        var flow        = deps.flow;
        var savvyId     = deps.savvyId;
        var fallbackUrl = deps.fallbackUrl;
        var skipOverlay = deps.skipOverlay || false;
        var hostEl      = root.querySelector('[data-intake-host]');

        var done          = false;
        var warmupDone    = skipOverlay;  // already "done" when skipping
        var intakeReady   = false;
        var revealed      = false;
        var revealedRef   = { value: false };
        var timeouts      = [];

        root.classList.remove('is-return');
        if (!skipOverlay) root.classList.add('is-warmup');

        function clearTimers() {
            for (var i = 0; i < timeouts.length; i++) clearTimeout(timeouts[i]);
            timeouts = [];
        }

        function buildFallbackUrl(reason) {
            if (!fallbackUrl) return '';
            try {
                var fb = new URL(fallbackUrl, window.location.origin);
                fb.searchParams.set('from', 'embeddables_wrapper');
                fb.searchParams.set('reason', reason || 'timeout');
                fb.searchParams.set('path', window.location.pathname + window.location.search + window.location.hash);
                return fb.toString();
            } catch (e) { return fallbackUrl; }
        }

        function goFallback(reason) {
            if (done || !fallbackUrl) return;
            done = true;
            clearTimers();
            window.removeEventListener('online',  onOnline);
            window.removeEventListener('offline', onOffline);
            window.location.href = buildFallbackUrl(reason);
        }

        function maybeReveal() {
            if (done || revealed || !warmupDone || !intakeReady) return;
            revealed        = true;
            revealedRef.value = true;
            done            = true;
            clearTimers();
            window.removeEventListener('online',  onOnline);
            window.removeEventListener('offline', onOffline);
            if (overlay) overlay.hide();
            root.classList.remove('is-warmup');
            root.classList.add('is-ready');
            if (hostEl) hostEl.classList.remove('is-hidden');
        }

        function onOnline() {
            if (done) return;
            if (warmupDone && !intakeReady && overlay) {
                overlay.setHeading('Almost ready');
                overlay.setMessage('Back online. Finalizing your intake\u2026');
            }
        }

        function onOffline() {
            if (done || !overlay) return;
            overlay.setHeading('Connection interrupted');
            overlay.setMessage('You appear to be offline. We\u2019ll keep trying\u2026');
        }

        window.addEventListener('online',  onOnline);
        window.addEventListener('offline', onOffline);

        // Build and start enhancements (click handler + session subscription)
        var enhCtx = window.BRXFlowEnhancements
            ? window.BRXFlowEnhancements.create({ savvyId: savvyId, sectionRoot: root })
            : null;
        if (enhCtx) enhCtx.start();

        // Warmup overlay animation (first-time visitors only)
        if (overlay && !skipOverlay) {
            overlay.showWarmup(function onWarmupComplete() {
                warmupDone = true;
                maybeReveal();
            });
        }

        // Flow root detection
        if (flow) {
            flow.start({
                enhancements:   enhCtx ? enhCtx.enhancements  : [],
                activeFlowRef:  enhCtx ? enhCtx.activeFlowRef : { current: null },
                revealedRef:    revealedRef,
                onFlowDetected: function () {
                    intakeReady = true;
                    maybeReveal();
                }
            });
        }

        // Load and initialise the Embeddables provider script
        if (loader) {
            loader.load(root, {
                onError: function () {
                    if (done) return;
                    if (overlay && !skipOverlay) {
                        overlay.setHeading('We\u2019re having trouble loading');
                        overlay.setMessage('Please try again.');
                    }
                    timeouts.push(setTimeout(function () { goFallback('init_error'); }, 1200));
                }
            });
        }

        // Timeout messaging + hard fallback (only meaningful when overlay is shown)
        if (!skipOverlay) {
            timeouts.push(setTimeout(function () {
                if (done || warmupDone || !navigator.onLine) return;
                if (overlay) {
                    overlay.setHeading('Almost ready');
                    overlay.setMessage('We\u2019re preparing the next step.');
                }
            }, Math.max(0, WARMUP_MS - 1500)));
        }

        timeouts.push(setTimeout(function () {
            if (done || intakeReady) return;
            if (!navigator.onLine) {
                if (overlay && !skipOverlay) {
                    overlay.setHeading('Connection interrupted');
                    overlay.setMessage('You appear to be offline. We\u2019ll keep trying\u2026');
                }
                return;
            }
            if (overlay && !skipOverlay) {
                overlay.setHeading('Almost ready');
                overlay.setMessage('Finalizing your intake\u2026');
            }
        }, SOFT_WAIT_MS));

        timeouts.push(setTimeout(function () {
            if (done) return;
            if (!navigator.onLine) {
                if (overlay && !skipOverlay) {
                    overlay.setHeading('Connection interrupted');
                    overlay.setMessage('Still offline. Please check your connection and try again.');
                }
                return;
            }
            goFallback('timeout');
        }, MAX_WAIT_MS));
    }

    window.BRXWrapperEntry = {
        getEntryMode: getEntryMode,
        start: start
    };
}());
